import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { supabaseAdmin } from '@/lib/supabase'

export function useSupabaseMetrics() {
  // Trailing 7 days (always complete data) - memoized to prevent constant recalculation
  const sevenDaysAgo = useMemo(() => {
    const date = new Date()
    date.setDate(date.getDate() - 7)
    date.setHours(0, 0, 0, 0) // Start of day to keep stable
    return date.toISOString()
  }, []) // Empty deps - only calculate once

  // Maps created this week
  const mapsQuery = useQuery({
    queryKey: ['metrics', 'maps', sevenDaysAgo],
    queryFn: async () => {
      const { count, error } = await supabaseAdmin
        .from('map_requests')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', sevenDaysAgo)
        .is('deleted_at', null)

      if (error) {
        console.error('Maps metric error:', error)
        throw error
      }
      return count ?? 0
    },
    staleTime: 60_000, // Consider data fresh for 1 minute
    gcTime: 300_000, // Keep in cache for 5 minutes
    refetchInterval: false, // Don't auto-refetch
    refetchOnWindowFocus: false, // TV doesn't need focus refetch
    refetchOnMount: false, // Don't refetch on component mount
  })

  // Flights this week - count unique source_recording_paths only
  const flightsQuery = useQuery({
    queryKey: ['metrics', 'flights', sevenDaysAgo],
    queryFn: async () => {
      // Get distinct source_recording_paths to avoid double counting
      const { data, error } = await supabaseAdmin
        .from('flights')
        .select('source_recording_path')
        .gte('created_at', sevenDaysAgo)
        .eq('is_deleted', false)
        .not('source_recording_path', 'is', null)

      if (error) {
        console.error('Flights metric error:', error)
        throw error
      }

      // Count unique source_recording_paths
      const uniquePaths = new Set(data?.map((f) => f.source_recording_path))
      return uniquePaths.size
    },
    staleTime: 60_000,
    gcTime: 300_000,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })

  // Total users (no created_at field in user_profiles, so we count all)
  const usersQuery = useQuery({
    queryKey: ['metrics', 'users'],
    queryFn: async () => {
      const { count, error } = await supabaseAdmin.from('user_profiles').select('*', { count: 'exact', head: true })

      if (error) {
        console.error('Users metric error:', error)
        throw error
      }
      return count ?? 0
    },
    staleTime: 300_000, // Consider fresh for 5 minutes
    gcTime: 600_000, // Keep in cache for 10 minutes
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })

  return {
    mapsCount: mapsQuery.data,
    flightsCount: flightsQuery.data,
    usersCount: usersQuery.data,
    isLoading: mapsQuery.isLoading || flightsQuery.isLoading || usersQuery.isLoading,
    isError: mapsQuery.isError || flightsQuery.isError || usersQuery.isError,
  }
}
